<template>
  <div class="cartItem">
    <div class="requestAt mb5 notranslate">
      <i class="orderId">{{locale.reservation_order_id}} {{order.order_id}}</i>
      <p class="paymentStatus">
        {{status(order.order_status)}}: {{setDateTimeFormat(order.createat)}}
      </p>
    </div>
    <div class="inner">
      <div class="row">
        <span class="label toCapitalize notranslate">{{locale.reservation_use_space}}</span>
        <div class="value">{{summary.space.name[lang]}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_use_date}}</span>
        <div class="value">{{summary.displayDate}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_use_time}}</span>
        <div class="value">{{summary.time.name}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_use_person}}</span>
        <div class="value">{{summary.person}}</div>
      </div>
      <div class="row">
        <span class="label toCapitalize notranslate">{{locale.reservation_use_section}}<br>{{locale.reservation_use_place}}</span>
        <div v-if="summary.seats && !summary.autoAssign" class="value">
          <div v-for="(seat, index) in summary.seats" :key="index">
            {{seat.section ? seat.section.name[lang] : '-'}}
            <span v-if="!summary.autoAssign && (summary.showPlaceName || summary.showSeatName)">
              -
              <span v-if="summary.showPlaceName">{{seat.place ? seat.place.name[lang] : '-'}}</span>
              <span v-if="summary.showPlaceName && summary.showSeatName"> / </span>
              <span v-if="summary.showSeatName">{{seat.seat ? seat.seat.name[lang] : '-'}}</span>
            </span>
          </div>
        </div>
        <div v-else class="value">
          {{summary.section.name[lang]}}
          <span v-if="!summary.autoAssign && (summary.showPlaceName || summary.showSeatName)">
              -
            <span v-if="summary.showPlaceName">{{summary.place.name[lang]}}</span>
            <span v-if="summary.showPlaceName && summary.showSeatName"> / </span>
            <span v-if="summary.showSeatName">{{summary.seat.name[lang]}}</span>
          </span>
        </div>
      </div>
      <div v-if="summary.firstName" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_first_name}}</span>
        <div class="value">{{summary.firstName}}</div>
      </div>
      <div v-if="summary.lastName" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_last_name}}</span>
        <div class="value">{{summary.lastName}}</div>
      </div>
      <div v-if="summary.phone" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_phone}}</span>
        <div class="value">{{summary.phone}}</div>
      </div>
      <div v-if="summary.email" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_email}}</span>
        <div class="value">{{summary.email}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_reservation_date}}</span>
        <div class="value">{{setDateTimeFormat(order.createat)}}</div>
      </div>
      <!-- <div class="row">
        <Qrcode
          :reservationId="order.order_id"
          :scale="4"  />
      </div> -->
      <div v-if="order.cancelled && order.cancelledat" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_cancel_date}}</span>
        <div class="value">{{setDateTimeFormat(order.cancelledat)}}</div>
      </div>
      <div v-if="cancellable && cancellable.isCancel" class="row notranslate">
        <div v-if="cancellable.ready">
          <button
            type="submit"
            class="btn btnRound sizeS btnWhite"
            :disabled="!cancellable.isCancellable"
            @click="cancel">{{locale.reservation_cancel}}</button>
          <p class="cancelMessage mt5">{{cancellable.message}}</p>
        </div>
        <p v-if="order.cancelled && order.cancelledat" class="cancelMessage">
          {{locale.reservation_already_cancel}}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment/moment'
import { DateTime } from 'luxon'
import regExp from '@/common/regExp'
import Qrcode from '@/components/Reservation/Qrcode'

export default {
  name: 'ReservationItem',
  props: [
    'order',
    'lang',
    'locale'
  ],
  components: {
    Qrcode
  },
  computed: {
    summary () {
      return this.order.ordersheet.content
    },
    dateFormat () {
      const setting = this.$store.state.common.setting
      const date = setting.dateFormat[setting.dateformatDefault]
      return date
    },
    format () {
      const time = 'HH:mm:ss'
      return `${this.dateFormat} ${time}`
    },
    timezone () {
      return this.$store.state.siteInfo.timezoneId || ''
    }
  },
  created () {
    this.init()
  },
  data () {
    return {
      cancellable: null
    }
  },
  methods: {
    init () {
      const property = this.order.reservationSpaceId
      function getTimeDisplay (hhmm) {
        const time = hhmm.toString()
        return `${time.substr(0, 2)}:${time.substr(2, 2)}`
      }
      this.$store.dispatch('reservation/getReservationPropertyInfo', property).then((info) => {
        if (info) {
          // 예약취소 가능여부 확인
          const ready = this.order.order_status === 'ready'
          const date = this.order.ordersheet.content.date
          const time = this.order.ordersheet.content.time.id.substr(0, 4)
          const isCancel = info.cancel
          const cancelDay = info.cancelDay
          const cancelTime = info.cancelTime
          const endTime = moment().set({ hour: time.substr(0, 2), minute: time.substr(2, 2) }).add(-cancelTime, 'minutes')
          const beforeToday = moment().isBefore(moment(date).add(-cancelDay, 'days').set({ hour: 0, minute: 0 }))
          const isToday = moment().format('YYYY-MM-DD') === date
          const isTime = moment().isBefore(endTime)
          const limitDay = cancelDay > 0 ? this.setDateFormat(moment(date).add(-cancelDay, 'days').format('YYYY-MM-DD')) : this.setDateFormat(date)
          const limitTime = cancelDay > 0 ? '' : getTimeDisplay(endTime.format('HHmm'))
          this.cancellable = {
            ready: ready,
            isCancel: isCancel,
            cancelDay: cancelDay,
            beforeToday: beforeToday,
            isToday: isToday,
            isTime: isTime,
            isCancellable: ready && (beforeToday || (cancelDay <= 0 && isToday && isTime)),
            limitDay: limitDay,
            limitTime: limitTime,
            message: this.locale.message_reservation_cancel_time.replace('{var}', `${limitDay} ${limitTime}`)
          }
        }
      })
    },
    setDateFormat (date) {
      return moment(date).format(this.dateFormat.toUpperCase())
    },
    setDateTimeFormat (iso) {
      const submitAt = DateTime.fromISO(iso || moment().toISOString(), { zone: this.timezone })
      return submitAt.toFormat(this.format)
    },
    datetimeFormat (iso) {
      DateTime.fromISO(iso, { zone: this.timezone })
    },
    displayValue (order) {
      const lang = this.lang
      let value = order.value
      switch (order.type) {
        case 'rate':
          value = `${value.value} / ${value.max}`
          break
        case 'radio':
          value = value.label[lang] + (value.quantity ? ` (${value.quantity})` : '')
          break
        case 'checkbox':
          value = value.map(x => {
            const label = x.label[lang]
            const quantity = x.quantity ? `(${x.quantity})` : ''
            return `${label} ${quantity}`
          })
          break
        case 'select':
          value = value.label ? value.label[lang] : value
          break
        case 'calendar':
          value = DateTime.fromISO(value).toFormat(this.dateFormat)
          break
      }
      return value
    },
    priceFormat (price) {
      const code = this.currencyCode
      return regExp.priceFormat(price, code)
    },
    isEmpty (str) {
      return regExp.replaceAllEmpty(str).length <= 0
    },
    status (orderStatus) {
      const status = `reservation_status_${orderStatus}`
      return this.locale[status]
    },
    cancel () {
      const id = this.order.order_uuid
      if (window.confirm(this.locale.confirm_reservation_cancel)) {
        this.$emit('cancel', id)
        this.init()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.content .cartItem {
  .inner {
    display: block;
  }
  .row {
    display: flex;
    & + .row {
      margin-top: 15px;
    }
  }
  .label {
    font-weight: bold;
  }
  .value {
    margin-left: 1em;
  }
}
.cancelMessage {
  font-size: 12px;
  color: $color-lgrey;
}
</style>
